import React, { useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';
import { useParams, useNavigate } from 'react-router-dom';
import { urlSearchParams } from '@utility/utills';
import { APP_CONFIG } from '@constants';
import { ReactComponent as InfoLogo } from '@assets/images/info.svg';
import { TouchableButton, TouchableDiv } from '@components/common/Touchable';
import { LABEL } from '@veriables';
import {
  toggleNestedModifierFlag,
  pushModifierToItem,
  setNestedModifierArray,
  editModifiers,
  setGlobalSelectedModifierId,
  getGlobalModifierData,
  setFunctionModifierId,
} from '@reducers/orders';
import SearchComponent from '@components/common/search/SearchComponentRevamp';
import { flowType } from '@utility/userInfo';
import {
  getAlphabets,
  getCountOfAlphabets,
  getCountWithoutStartingWithAlphabet,
} from '../GlobalModifier/GlobalModifierActionHandler';
import NutritionInfoV2 from '@components/OrderItemDetailNewUI/V2/NutritionInfoV2';
import FullWidthModal from '@components/common/Modal/FullWidthModal';
import styles from './OrderItemHeader.module.scss';
import ModFunctionBar from '../OrderItemModifiers/ModFunctionBar';
import { ReactComponent as RightArrow } from '@assets/images/Fireboard/rightnavarrow.svg';

const ALPHABETS = getAlphabets();

/**
 * Memoized pill buttons for each global modifier group.
 * Shown in normal (non-global-mod-active) mode.
 */
export const GlobalModifierButtons = React.memo(
  ({
    globalModifierGroupIds,
    modifierGroupsHashTable,
    serverAppTheme,
    onButtonClick,
    setActiveMoreMenu,
  }) => (
    <div className="d-flex flex-row o-group-menu overflowX w-100 align-items-center pb-12">
      {globalModifierGroupIds.map(modFunc => (
        <TouchableButton
          key={modFunc}
          className={cn(
            styles.globalModBtn,
            'd-flex flex-shrink-0 inline-flex text-nowrap pl-2 pr-1 mr-1 fs_12 fw-400 justify-content-between align-items-center'
          )}
          onClick={e => {
            e.preventDefault();
            onButtonClick(modFunc);
            if (setActiveMoreMenu) setActiveMoreMenu(null);
          }}
          style={{
            color: serverAppTheme?.primary_text,
            border: '1px solid #BEBEBE',
            background: serverAppTheme?.card_bg,
          }}
          rippleEffect={true}
        >
          {modifierGroupsHashTable[modFunc]?.name}
          <RightArrow height={25} width={25} fill="var(--primary-icon-color)" />
        </TouchableButton>
      ))}
    </div>
  )
);

/**
 * Memoized A–Z + # alphabet filter bar.
 * Shown when a global modifier is active.
 */
const AlphabetFilter = React.memo(
  ({
    searchAlphabet,
    serverAppTheme,
    modifierGroupsHashTable,
    modifiersHashTable,
    globalSelectedModifierId,
    onSelect,
  }) => {
    const othersCount = getCountWithoutStartingWithAlphabet(
      modifierGroupsHashTable,
      modifiersHashTable,
      globalSelectedModifierId
    );

    return (
      <div
        className="col-12 rounded px-0"
        style={{
          backgroundColor: serverAppTheme?.card_bg,
          border: serverAppTheme?.card_border,
        }}
      >
        <div className="col-12 px-0 h-100 d-flex flex-row flex-wrap">
          {ALPHABETS.map((alphabet, index) => {
            const count = getCountOfAlphabets(
              alphabet,
              modifierGroupsHashTable,
              modifiersHashTable,
              globalSelectedModifierId
            );
            const isSelected = searchAlphabet === alphabet;
            return (
              <TouchableDiv
                key={alphabet}
                style={{
                  width: '3.7%',
                  background: isSelected
                    ? serverAppTheme?.search_field_bg
                    : serverAppTheme?.card_bg,
                }}
                className={cn(
                  count > 0 ? '' : styles.globalModDisabled,
                  'text-center d-flex align-items-center justify-content-center p-1 h-100',
                  index === 0 && 'rounded-left'
                )}
                role="presentation"
                onClick={() => {
                  if (count > 0) onSelect(alphabet);
                }}
              >
                <div
                  className="fw-600 fs_12"
                  style={{ textDecoration: isSelected ? 'underline' : '' }}
                >
                  {alphabet}
                </div>
              </TouchableDiv>
            );
          })}

          {/* # for non-alphabetic names */}
          <TouchableDiv
            style={{
              minWidth: '3.7%',
              color: serverAppTheme?.primary_text,
            }}
            className={cn(
              othersCount > 0 ? '' : styles.globalModDisabled,
              'text-center d-flex align-items-center justify-content-center p-1',
              searchAlphabet === 'others' ? styles.activeModifier : ''
            )}
            role="presentation"
            onClick={() => onSelect('others')}
          >
            <div className="fs-12 fw-600">#</div>
          </TouchableDiv>
        </div>
      </div>
    );
  }
);

const OrderItemHeader = props => {
  const params = useParams();
  const navigate = useNavigate();
  const { menuTypeId, menuItemId } = params ?? {};

  // const [info, setInfo] = useState(false);
  const [searchAlphabet, setSearchAlphabet] = useState(undefined);

  const itemInfo = useMemo(() => props.menuCard[menuTypeId]?.[menuItemId], [
    props.menuCard,
    menuTypeId,
    menuItemId,
  ]);

  const parentMenuItemInfo = useMemo(() => {
    const parentId = urlSearchParams('parentMenuItemId');
    return parentId
      ? props.menuCard[params.menuTypeId]?.[Number(parentId)]
      : null;
  }, [props.menuCard, params.menuTypeId]);

  const itemDetailsInfo = parentMenuItemInfo || itemInfo;

  const isServerApp = useMemo(() => flowType() === 'serverApp', []);

  const handleInfoOpen = useCallback(() => props?.setInfo(true), []);
  const handleInfoClose = useCallback(() => props?.setInfo(false), []);

  const modalStyle = useMemo(
    () => ({
      width: '100%',
      height: '100%',
      backgroundColor: props.serverAppTheme?.popUp_bg,
      border: props.serverAppTheme?.popupBorder,
      boxShadow: props.serverAppTheme?.popUp_box_shadow,
    }),
    [
      props.serverAppTheme?.popUp_bg,
      props.serverAppTheme?.popupBorder,
      props.serverAppTheme?.popUp_box_shadow,
    ]
  );

  const globalModifierGroupIds = useMemo(
    () => props.menusList?.[props.menuIndex]?.globalModifierGroupIds ?? [],
    [props.menusList, props.menuIndex]
  );

  /** Show global mod pill buttons only in serverApp, when not nested */
  const showGlobalModButtons = useMemo(
    () =>
      isServerApp &&
      !props.isNestedModifier &&
      globalModifierGroupIds.length > 0,
    [isServerApp, props.isNestedModifier, globalModifierGroupIds]
  );

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleGlobalModButtonClick = useCallback(
    modFunc => {
      props.setGlobalSelectedModifierId(modFunc).then(() => {
        props.getGlobalModifierData(true);
      });
    },
    [props.setGlobalSelectedModifierId, props.getGlobalModifierData]
  );

  const handleSearchChange = useCallback(
    searchStr => {
      if (searchStr) {
        props.getGlobalModifierData(false, '', searchStr);
      } else {
        // Cleared search → reload full list
        props.getGlobalModifierData(true);
      }
      setSearchAlphabet(undefined);
    },
    [props.getGlobalModifierData]
  );

  const handleAlphabetSelect = useCallback(
    alphabet => {
      setSearchAlphabet(alphabet);
      props.getGlobalModifierData(false, alphabet);
    },
    [props.getGlobalModifierData]
  );

  const handleCancel = useCallback(
    e => {
      e.preventDefault();
      if (props.isNestedModifier) {
        props.toggleNestedModifierFlag(false);
      }
      window.sessionStorage.removeItem('serverAppModifierGroupDisplayStyle');
      window.sessionStorage.removeItem('pendingItemNote');
      window.sessionStorage.removeItem('pendingModifierNote');
      props.setNestedModifierArray([]); // clear nested modifier items
      props.pushModifierToItem([], 'isNested');
      props.editModifiers({
        modifier: '',
        modifierGroupId: '',
        modifierName: '',
      });
      props.setFunctionModifierId(null);
      navigate(`${APP_CONFIG.BASE_URL}/orders`);
    },
    [
      props.isNestedModifier,
      props.toggleNestedModifierFlag,
      props.setNestedModifierArray,
      props.pushModifierToItem,
      props.setFunctionModifierId,
      navigate,
    ]
  );

  // ── Render: normal header ──────────────────────────────────────────────────
  return (
    <>
      <div className="col-12 px-0">
        {/* <div className={cn('d-flex fs_18 fw-500 justify-content-between align-items-center px-2 py-1', styles.modifierInfoheader)} style={{ color: props.serverAppTheme?.btn_font_color }}>
          <div className="col-8 px-0 text-truncate">{itemDetailsInfo?.menuItem?.name}</div>
          <div className="col-4 pr-0 d-flex align-items-center justify-content-end">
             {props.auth?.serverAppFeatures?.displayPrice && (
                <div>${itemDetailsInfo?.price.toFixed(2)}</div>
              )}

            <div className="pl-2">
              <TouchableDiv
                className="d-flex align-items-center justify-content-center h-90 rounded px-0"
                style={{
                  backgroundColor: props.serverAppTheme?.global_mod_tile,
                  height: '29px',
                  width: '29px',
                }}
                role="presentation"
                onClick={e => {
                  e.preventDefault();
                  setInfo(!info);
                }}
                rippleEffect={true}
              >
                <InfoLogo
                  fill={props?.serverAppTheme?.iconcolor}
                  alt="help"
                  className="w-100"
                />
              </TouchableDiv>
            </div>

          </div>
        </div> */}

        {props.globalSelectedModifierId && (
          <div className="col-12 px-0">
            {/* Search box */}
            <div className="col-12 px-0 mt-1" style={{ height: '2.5rem' }}>
              <SearchComponent
                onChange={handleSearchChange}
                customClass1="py-1"
                customClass2="fs-16"
                serverAppTheme={props.serverAppTheme}
              />
            </div>

            {/* Alphabet filter */}
            <div className="col-12 px-0 mt-1">
              <AlphabetFilter
                searchAlphabet={searchAlphabet}
                serverAppTheme={props.serverAppTheme}
                modifierGroupsHashTable={props.modifierGroupsHashTable}
                modifiersHashTable={props.modifiersHashTable}
                globalSelectedModifierId={props.globalSelectedModifierId}
                onSelect={handleAlphabetSelect}
              />
            </div>
          </div>
        )}

        {/* {!props.globalSelectedModifierId && <ModFunctionBar />} */}

        {/* Global modifier pill buttons */}
        {/* {showGlobalModButtons && !props.globalSelectedModifierId && (
          <GlobalModifierButtons
            globalModifierGroupIds={globalModifierGroupIds}
            modifierGroupsHashTable={props.modifierGroupsHashTable}
            serverAppTheme={props.serverAppTheme}
            onButtonClick={handleGlobalModButtonClick}
          />
        )} */}
      </div>
      
      {props?.info && (
        <FullWidthModal
          isOpen={props?.info}
          toggleModal={handleInfoClose}
          customClass2="h-100 w-100"
          style={modalStyle}
        >
          <NutritionInfoV2
            itemInfo={itemDetailsInfo}
            serverAppTheme={props.serverAppTheme}
            seat={props.seat}
            onClose={handleInfoClose}
          />
        </FullWidthModal>
      )}
    </>
  );
};

const mapStateToProps = ({ orders, theme, oauth }) => ({
  menuCard: orders.menuCardList,
  serverAppTheme: theme.serverAppTheme,
  isNestedModifier: orders.isNestedModifier,
  menusList: orders.menusList,
  menuIndex: orders.menuIndex,
  globalSelectedModifierId: orders.globalSelectedModifierId,
  modifierGroupsHashTable: orders.modifierGroupsHashTable,
  modifiersHashTable: orders.modifiersHashTable,
  globalModEnabled: orders.globalModEnabled,
  seat: orders.seats,
  auth: oauth.authInfo,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      toggleNestedModifierFlag,
      pushModifierToItem,
      setNestedModifierArray,
      editModifiers,
      setGlobalSelectedModifierId,
      getGlobalModifierData,
      setFunctionModifierId,
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(OrderItemHeader));
